import { useState } from "react";
import { useMutation } from "@apollo/client/react";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { PERSONALIZE_SESSION } from "@/graphql/mutations/sessions";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  PERSONALIZATION_HINT,
  PERSONALIZATION_PLACEHOLDER,
  PERSONALIZATION_PRIVACY_HINT,
} from "@/lib/personalization";

/**
 * Rewording a session's questions around the learner — a grown-up's step, run
 * once the exercises are already picked.
 *
 * Deliberately not part of generating a session: it is a real AI call worth ten
 * seconds or more, and a child pressing "start" should never wait on it. The
 * session is complete and solvable before this button is ever pressed.
 *
 * It lives behind a button rather than on the page because it is one occasional
 * action with a text field attached: laid out flat it took a card's worth of
 * room above the questions on every visit, including the visits where nobody
 * was going to press it.
 *
 * This box is the only place these details are ever typed. They used to also be
 * kept on the learner's card, and stored there they outlived the session they
 * were written for — a standing description of a child, sent to an external AI
 * provider on every personalized session, whether or not anybody still meant it.
 * Typed here they are scoped to the one session a grown-up is preparing.
 */
export function SessionPersonalization({
  sessionId,
  status,
  personalizedCount,
  totalProblems,
  sessionProfile,
  onDone,
}: {
  sessionId: string;
  status?: string | null;
  personalizedCount?: number | null;
  totalProblems: number;
  sessionProfile?: string | null;
  onDone?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState(sessionProfile ?? "");
  const [personalizeSession, { loading }] = useMutation(PERSONALIZE_SESSION);

  const done = status === "READY" && (personalizedCount ?? 0) > 0;

  async function run() {
    if (!profile.trim()) {
      toast.error("Parašykite, ką AI turi žinoti apie mokinį");
      return;
    }

    try {
      const { data } = await personalizeSession({
        variables: { id: sessionId, profile: profile.trim() || null },
      });
      const errors: string[] = data?.personalizeSession?.errors ?? [];
      if (errors.length > 0) {
        toast.error(errors.join("; "));
        return;
      }
      const count = data?.personalizeSession?.session?.personalizedProblemCount ?? 0;
      toast.success(
        count > 0
          ? `Pritaikyta užduočių: ${count} iš ${totalProblems}`
          : "Nieko nereikėjo pritaikyti — užduotys liko tokios, kokios yra"
      );
      setOpen(false);
      onDone?.();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Nepavyko pritaikyti užduočių");
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !loading && setOpen(next)}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Sparkles className="mr-2 h-4 w-4 text-amber-600 dark:text-amber-400" />
          Pritaikyti mokiniui
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Pritaikyti užduotis mokiniui</DialogTitle>
          <DialogDescription>{PERSONALIZATION_HINT}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {done && (
            <p className="text-sm text-amber-700 dark:text-amber-400">
              Pritaikyta {personalizedCount} iš {totalProblems} užduočių.
            </p>
          )}
          {status === "FAILED" && (
            <p className="text-sm text-red-600 dark:text-red-400">
              Paskutinis bandymas nepavyko — mokinys mato originalų tekstą.
            </p>
          )}

          <div className="space-y-2">
            {/* No name in the label: „apie {studentName}" needs the accusative
                („apie Povilą"), and a name cannot be declined generically — so
                the sentence is written to avoid the case rather than get it
                wrong every time. The dialog is already about this learner. */}
            <Label htmlFor="session-personalization-profile" className="text-xs">
              Ką AI turi žinoti apie mokinį — tik šiai sesijai
            </Label>
            <Textarea
              id="session-personalization-profile"
              value={profile}
              onChange={(event) => setProfile(event.target.value)}
              placeholder={PERSONALIZATION_PLACEHOLDER}
              rows={3}
              disabled={loading}
            />
            <p className="text-xs text-muted-foreground">{PERSONALIZATION_PRIVACY_HINT}</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={loading}>
            Atšaukti
          </Button>
          <Button onClick={run} disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
            {loading ? "Pritaikoma..." : done ? "Pritaikyti iš naujo" : "Pritaikyti"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
